/** Group conversation list for Ask AI sidebar (Today / Previous 7 Days / Older). */

function startOfLocalDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

export function groupConversationsByRecency(conversations = []) {
    const now = new Date();
    const todayStart = startOfLocalDay(now);
    const weekStart = new Date(todayStart);
    weekStart.setDate(weekStart.getDate() - 7);

    const groups = {
        today: [],
        previous7Days: [],
        older: [],
    };

    for (const conv of conversations) {
        const ts = conv?.updated_at || conv?.created_at;
        const d = ts ? new Date(ts) : null;
        if (!d || Number.isNaN(d.getTime())) {
            groups.older.push(conv);
            continue;
        }
        if (d >= todayStart) {
            groups.today.push(conv);
        } else if (d >= weekStart) {
            groups.previous7Days.push(conv);
        } else {
            groups.older.push(conv);
        }
    }

    return groups;
}

export function formatConversationTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';

    const now = new Date();
    const todayStart = startOfLocalDay(now);
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);

    if (d >= todayStart) {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    if (d >= yesterdayStart) {
        return 'Yesterday';
    }
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

export const ASK_AI_SUGGESTED_QUESTIONS = [
    'What are the most critical integration risks identified so far?',
    'Summarise the key unresolved decisions across all workstreams.',
    'Which open actions are overdue or at risk of slipping?',
    'What patterns emerge from the security and compliance notes?',
    'Compare the technical and commercial risks by severity.',
    'Which stakeholders have outstanding commitments to resolve?',
];

export function contextModeLabel(contextMode) {
    switch (contextMode) {
        case 'select_workstreams':
            return 'Workstreams';
        case 'custom':
            return 'Custom Sources';
        case 'entire_project':
        default:
            return 'Entire Project';
    }
}

/** Short sidebar/modal title from prompt body when none was provided. */
export function titleFromPromptText(text, max = 56) {
    const cleaned = String(text || '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!cleaned) return 'Untitled prompt';
    if (cleaned.length <= max) return cleaned;
    return `${cleaned.slice(0, max - 1).trim()}…`;
}

/** Strip inline [n] citation markers from assistant markdown for Ask Lens display. */
export function stripInlineCitationMarkers(text) {
    return String(text || '')
        .replace(/\[(\d+)\]\s*[-–—]\s*\[(\d+)\]/g, '')
        .replace(/\[(\d+)\]/g, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/ +\./g, '.')
        .replace(/ +,/g, ',')
        .replace(/ {2,}/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

export function contextChangeMessageText(label) {
    const clean = String(label || '').trim() || 'updated context';
    return `Context changed to ${clean}. Earlier answers in this chat may reflect a different source scope.`;
}

/** Normalize persisted assistant message into ask-results shape used by response UI. */
export function assistantMessageToAskData(message) {
    if (!message) return null;
    const citations = Array.isArray(message.citations) ? message.citations : [];
    const evidence = Array.isArray(message.evidence) ? message.evidence : [];
    const meta = message.meta && typeof message.meta === 'object' ? message.meta : {};
    return {
        question: null,
        final_answer: message.content || '',
        answer: message.content || '',
        citations,
        evidence,
        confidence: message.confidence || meta.confidence || '',
        trace_id: message.trace_id || null,
        retrieval_debug: meta.retrieval_debug || {},
        evidence_governance: meta.evidence_governance ?? null,
        alignment_analysis: meta.alignment_analysis ?? null,
        irl_claim_validation: meta.irl_claim_validation ?? null,
        suggested_actions: Array.isArray(meta.suggested_actions)
            ? meta.suggested_actions
            : null,
        response_actions: meta.response_actions ?? null,
        observation_candidates: Array.isArray(meta.observation_candidates)
            ? meta.observation_candidates
            : null,
        response_blocks: Array.isArray(meta.response_blocks)
            ? meta.response_blocks
            : Array.isArray(meta.blocks)
              ? meta.blocks
              : null,
        intelligence_source: true,
    };
}
